import * as React from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import { getConfigValue, getServerOverride, serializeProps } from '../lib/index.js';
import { Placeholder } from './placeholder.js';
import { renderMicrofrontend, unmountMicrofrontend } from './render.js';
export function createReactClient({ mfId, server, onError, ErrorView }, options) {
    const Client = ({ children, ...props }) => {
        const [error, setError] = useState(null);
        const instanceId = `${mfId}-${props.instanceId ?? 'default'}`;
        const { serverUrl, prerenderData } = useMemo(() => {
            return {
                serverUrl: getServerUrl(server, props.env),
                prerenderData: getPrerenderContent(instanceId),
            };
        }, []);
        useEffect(() => {
            let placeholder = null;
            if (props.render !== false && !prerenderData) {
                renderMicrofrontend(instanceId, serverUrl, props, options)
                    .then(elem => {
                    placeholder = elem;
                })
                    .catch(err => {
                    setError(err);
                    onError?.(err);
                    if (props.env !== 'prod') {
                        // eslint-disable-next-line no-console
                        console.error(`Failed to render '${instanceId}'`, err);
                    }
                });
            }
            return () => {
                if (placeholder) {
                    unmountMicrofrontend(placeholder);
                }
            };
        }, [props.render]);
        const ErrorViewComponent = () => {
            return React.createElement(React.Fragment, null, ErrorView ? React.createElement(ErrorView, { error: error }) : React.createElement("div", null, "Unknown error occurred. Please try again."));
        };
        return (React.createElement(React.Fragment, null,
            React.createElement("script", { "data-mf-id": instanceId, "data-mf-role": "data", type: "application/json", dangerouslySetInnerHTML: {
                    __html: getPropsData(props),
                } }),
            React.createElement(Placeholder, { serverUrl: serverUrl, instanceId: instanceId, noPrerender: props.noPrerender, prerenderContent: prerenderData, loader: error ? React.createElement(ErrorViewComponent, null) : React.createElement(React.Fragment, null, children) })));
    };
    // only re-render if render prop changes
    return memo(Client, (prevProps, props) => prevProps.render === props.render);
}
function getServerUrl(server, env) {
    const serverValue = getConfigValue(server, env);
    if (env === 'prod' || typeof window === 'undefined') {
        return serverValue;
    }
    const cookies = new Map();
    document.cookie.split(';').forEach(cookie => {
        const [name, value] = cookie.trim().split('=');
        cookies.set(name, value);
    });
    const { pathname } = new URL(serverValue);
    const serverOverride = getServerOverride(pathname, cookies, env);
    return serverOverride ? serverOverride + pathname : serverValue;
}
function getPropsData(props) {
    const params = serializeProps(props);
    return JSON.stringify(params);
}
function getPrerenderContent(instanceId) {
    if (typeof document === 'undefined') {
        return;
    }
    const placeholder = document.querySelector(`[data-mf-id="${instanceId}"][data-mf-role="placeholder"]`);
    if (!placeholder) {
        return;
    }
    const isLoader = placeholder.querySelector(`[data-mf-id="${instanceId}"][data-mf-role="loader"]`);
    return isLoader ? undefined : placeholder.innerHTML;
}
